/**
 * Month-over-month change helpers for the Google panels, the Rate of growth
 * panel and the clients table. A null month is a gap (DD-021): it yields no
 * change, never a change against zero.
 */

import type { GrowthSource } from "../hooks/useGrowth";

export type ChangeTone = "up" | "down" | "neutral";

export const GROWTH_SOURCE_LABEL: Record<GrowthSource, string> = {
  social: "Social",
  businessProfile: "Business Profile",
  website: "Website",
};

/** "Website sync is behind, so growth leaves it out", or the plural for
 *  several sources; null when nothing is overdue. */
export const overdueNote = (overdue: ReadonlyArray<GrowthSource>): string | null => {
  if (overdue.length === 0) return null;
  const names = overdue.map((source) => GROWTH_SOURCE_LABEL[source]);
  const joined =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return names.length === 1
    ? `${joined} sync is behind, so growth leaves it out`
    : `${joined} syncs are behind, so growth leaves them out`;
};

/** A figure's `change` (ReportStat, ChangePill). `scale` turns a ratio (0.42) into points
 *  (42) so a rate's delta reads as percentage points. Undefined = no chip;
 *  a null percentage = no base (previous month 0), so the chip shows the
 *  delta alone. */
export const metricChange = (
  current: number | null | undefined,
  previous: number | null | undefined,
  scale = 1,
): { value: number; percentage: number | null } | undefined => {
  if (current == null || previous == null) return undefined;
  const delta = current - previous;
  return {
    value: Math.round(delta * scale * 100) / 100,
    percentage: previous > 0 ? (delta / previous) * 100 : null,
  };
};

/** A ratio's change (0.42 → 0.45) in percentage points, to two decimals,
 *  with no relative %: "+3.3 pp" reads plainly, "+7%" of a rate does not. */
export const pointsChange = (
  current: number | null | undefined,
  previous: number | null | undefined,
): { value: number; percentage: null } | undefined => {
  if (current == null || previous == null) return undefined;
  return { value: Math.round((current - previous) * 10000) / 100, percentage: null };
};

/** A points delta for a figure's formatChange: "3.3 pp", or two decimals
 *  under 0.1 so a small move on a rate shown as "1.48%" still reads,
 *  "0.04 pp" (the pill adds the sign). */
export const formatPoints = (points: number): string =>
  `${points.toFixed(Math.abs(points) < 0.1 ? 2 : 1)} pp`;

/** A change pill's text and tone (ChangePill): "+12 (+5%)", or "no
 *  change" (no tone) when the delta rounds to zero at cents or hundredths
 *  of a point, below what the figures print. Its percentage must round to
 *  zero too, so a small ratio's real move (a CTR of 0.003) still shows.
 *  `trend` overrides the sign's tone where lower is the win (CPC, CPM). */
export const describeChange = (
  change: { value: number; percentage: number | null },
  formatChange: (value: number) => string = (value) => value.toLocaleString(),
  trend?: ChangeTone,
): { label: string; tone: ChangeTone | undefined } => {
  const isZero =
    Math.round(change.value * 100) === 0 &&
    (change.percentage == null || Math.round(change.percentage) === 0);
  if (isZero) return { label: "no change", tone: undefined };
  // Math.round, not toFixed: toFixed prints a small fall as "-0", while a
  // template prints Math.round's -0 as "0".
  const percent = change.percentage != null ? Math.round(change.percentage) : null;
  const changePercent = percent != null ? ` (${percent > 0 ? "+" : ""}${percent}%)` : "";
  return {
    label: `${change.value > 0 ? "+" : ""}${formatChange(change.value)}${changePercent}`,
    tone: trend || (change.value > 0 ? "up" : change.value < 0 ? "down" : "neutral"),
  };
};

/** Percent change, or null when either month is missing or the previous
 *  month is 0 (no base to compare against). */
export const changePct = (
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null => {
  if (current == null || previous == null || previous <= 0) return null;
  return ((current - previous) / previous) * 100;
};

/** "+12%", "-3%", "0%", or "-" for a gap. Rounds to whole percent. */
export const formatSignedPct = (pct: number | null | undefined): string => {
  if (pct == null) return "-";
  const rounded = Math.round(pct);
  if (rounded === 0) return "0%";
  return `${rounded > 0 ? "+" : ""}${rounded}%`;
};

/** A growth rate: one decimal under 10% ("+3.4%"), whole above ("+12%"),
 *  "0%" when it rounds to zero, "-" for a gap. */
export const formatSignedRate = (rate: number | null | undefined): string => {
  if (rate == null) return "-";
  const tenths = Math.round(rate * 10) / 10;
  if (Math.abs(tenths) >= 10) return formatSignedPct(rate);
  if (tenths === 0) return "0%";
  return `${tenths > 0 ? "+" : ""}${tenths.toFixed(1)}%`;
};

/** Tone follows the rounded figure, so a "0%" label is never coloured. */
export const changeTone = (pct: number | null | undefined): ChangeTone => {
  if (pct == null) return "neutral";
  const rounded = Math.round(pct);
  if (rounded > 0) return "up";
  if (rounded < 0) return "down";
  return "neutral";
};

/** Growth rates within this band count as flat, the same band the agency
 *  footer's growing / flat / declining counts use. */
export const GROWTH_FLAT_BAND = 2;

/** Tone for a growth rate: green above +2, red below -2, muted within, so a
 *  client counted flat is never coloured. */
export const growthTone = (rate: number | null | undefined): ChangeTone => {
  if (rate == null) return "neutral";
  if (rate > GROWTH_FLAT_BAND) return "up";
  if (rate < -GROWTH_FLAT_BAND) return "down";
  return "neutral";
};

/** An ISO date ("2026-11-05") as "5 November", read as a calendar day so no
 *  time zone can shift it. */
export const formatDayMonth = (isoDate: string): string => {
  const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  });
};

// Fixed, not toLocaleDateString: en-GB short months print "Sept" on newer ICU.
export const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A same-days window ending on an ISO day: "1–26 Oct", or "1 Oct" when it
 *  is the 1st. Read from the string, so no time zone can shift it. */
const formatWindow = (through: string): string => {
  const [, month, day] = through.slice(0, 10).split("-").map(Number);
  const range = day === 1 ? "1" : `1–${day}`;
  return `${range} ${MONTH_SHORT[month - 1]}`;
};

/** A month in progress against the same days of the month before:
 *  "1–26 Oct vs 1–26 Sep". */
export const formatWindows = (through: string, previousThrough: string): string =>
  `${formatWindow(through)} vs ${formatWindow(previousThrough)}`;
/** A month the report covers only in part, set against a share of the
 *  month before: days covered ÷ days in that month, above 1 when this month
 *  has covered more days than the month before has (30 Mar vs 28 Feb). */
export interface ProRata {
  daysCovered: number;
  previousDays: number;
  share: number;
  previousMonth: number;
}

/** The pro-rata share for a report whose period ends on `periodEnd`: null
 *  when the period reaches the month's last day (a whole month compares
 *  with a whole month) or does not end inside the month. Flows (posts,
 *  impressions, spend) scale by it; levels (followers) and ratios do not. */
export const proRataFor = (
  periodEnd: string | null | undefined,
  year: number,
  month: number,
): ProRata | null => {
  if (!periodEnd) return null;
  const [endYear, endMonth, endDay] = periodEnd.slice(0, 10).split("-").map(Number);
  if (endYear !== year || endMonth !== month) return null;
  // Day 0 of the next month is this month's last day.
  if (endDay >= new Date(year, month, 0).getDate()) return null;
  const previousMonth = month === 1 ? 12 : month - 1;
  const previousDays = new Date(month === 1 ? year - 1 : year, previousMonth, 0).getDate();
  return { daysCovered: endDay, previousDays, share: endDay / previousDays, previousMonth };
};

/** A pro-rata comparison's header note: "vs Sep pro-rata · 29 of 30 days",
 *  "vs Feb pro-rata · 30 days vs 28" once this month has covered more days
 *  than the month before has, or "vs Sep · 30 days each" when the counts
 *  match and nothing is scaled. */
export const proRataLabel = (proRata: ProRata): string => {
  const { daysCovered, previousDays } = proRata;
  const previous = MONTH_SHORT[proRata.previousMonth - 1];
  if (daysCovered === previousDays) return `vs ${previous} · ${daysCovered} days each`;
  const days =
    daysCovered < previousDays
      ? `${daysCovered} of ${previousDays} days`
      : `${daysCovered} days vs ${previousDays}`;
  return `vs ${previous} pro-rata · ${days}`;
};

/** The month before's flow scaled to the days covered, rounded to its unit:
 *  a whole count, or cents for money (`decimals` 2), so a delta never
 *  prints as "+762.667". */
export const proRataBaseline = (previous: number, share: number, decimals = 0): number => {
  const unit = 10 ** decimals;
  return Math.round(previous * share * unit) / unit;
};

/** True once the local calendar day is after an ISO date: a source whose
 *  month should be final by now is behind, not in progress. */
export const isAfterDay = (isoDate: string, now: Date = new Date()): boolean => {
  const pad = (n: number) => String(n).padStart(2, "0");
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return today > isoDate.slice(0, 10);
};

/** Every pillar's change on one line, for a title attribute:
 *  "Audience +4% · Actions -". */
export const pillarSummary = (
  pillars: ReadonlyArray<{ label: string; changePct: number | null }>,
): string =>
  pillars.map((p) => `${p.label} ${formatSignedRate(p.changePct)}`).join(" · ");

/** Cents to EUR. Whole euros by default; `precise` keeps the cents. */
export const formatEuroMinor = (minor: number, precise = false): string =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: precise ? 2 : 0,
    maximumFractionDigits: precise ? 2 : 0,
  }).format(minor / 100);
